/*
 * Copyright (c) 2024. Tooling Team
 */

import { transformMVApiResponse } from "../utils/funcs";
import {
  useLazyGetMapByIdQuery,
  useSearchMapsMutation,
} from "../app/services/mapsApi";
import { useLazyGetConceptSetQuery } from "../app/services/conceptSetsApi";

const missingStandardCodeText =
  "Cannot clone these rows because one or more standard codes are not part of the selected target concept set:";
const duplicateLocalKeyText =
  "Cannot clone these rows because one or more local code + local source system combinations already exist in the selected target map:";

export default () => {
  const [searchMapsApi] = useSearchMapsMutation();
  const [mapByIdApi] = useLazyGetMapByIdQuery();
  const [conceptSetApi] = useLazyGetConceptSetQuery();

  async function fetchValidation(searchArr, cloneRows) {
    try {
      const { data } = await searchMapsApi({
        attributes: searchArr,
      });

      if (data.records.length > 0) {
        const valid = await fetchAreRowsValid(
          `${data.records[0].uuid}?version=MAX`,
          cloneRows
        );
        return valid;
      }
    } catch (error) {
      console.error(error);
    }
  }
  async function fetchAreRowsValid(mapID, cloneRows) {
    try {
      // Fetch the map object
      const mapByApiResponse = await mapByIdApi({
        mapID,
        moduleName: "clone",
      }).unwrap();
      const data = mapByApiResponse
        ? JSON.parse(JSON.stringify(mapByApiResponse))
        : {};
      transformMVApiResponse(data.mapElements.records);
      const getLocalKey = (row) => {
        const sourceSystem = row.sourceSystem || row.localSourceSystem || "";
        const localCode = row.localCode || "";
        return `${sourceSystem}\u0000${localCode}`;
      };
      const getLocalKeyDisplay = (row) => {
        const sourceSystem = row.sourceSystem || row.localSourceSystem || "";
        const localCode = row.localCode || "";
        return `${localCode} + ${sourceSystem || "<blank>"}`;
      };
      const destLocalKeys = new Set(data.mapElements.records.map(getLocalKey));
      const duplicateLocalKeys = Array.from(
        new Set(
          cloneRows
            .filter((row) => destLocalKeys.has(getLocalKey(row)))
            .map(getLocalKeyDisplay)
        )
      );
      if (duplicateLocalKeys.length > 0) {
        return {
          valid: false,
          message: `${duplicateLocalKeyText} ${duplicateLocalKeys
            .map((keyCombo) => `[${keyCombo}]`)
            .join(", ")}.`,
        };
      }

      return await validateStandardCodes(data.conceptSetId, cloneRows);
    } catch (error) {
      console.error(error);
    }
  }
  // Validate that all standard codes in the cloneRows exist in the target concept set
  async function validateStandardCodes(conceptSetId, cloneRows) {
    if (!conceptSetId) {
      return true;
    }

    const conceptSet = await conceptSetApi(conceptSetId).unwrap();
    const validStandardCodes = new Set(
      (conceptSet.values || []).map((concept) => concept.code)
    );
    const missingStandardCodes = Array.from(
      new Set(
        cloneRows
          .map((row) => row.standardCode)
          .filter(
            (standardCode) => standardCode && standardCode !== "UNCHANGED"
          )
          .filter((standardCode) => !validStandardCodes.has(standardCode))
      )
    );

    if (missingStandardCodes.length > 0) {
      return {
        valid: false,
        message: `${missingStandardCodeText} ${missingStandardCodes
          .map((standardCode) => `[${standardCode}]`)
          .join(", ")}.`,
      };
    }

    return true;
  }

  return {
    fetchValidation,
  };
};
